import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAiSettings, useSaveAiSettings } from '@/queries/settings';
import type { AiEffort, AiModelChoice, AiSettings, AiSettingsResponse, SteppedAiModelChoice } from '@/queries/settings';

function groupByProvider(catalog: AiSettingsResponse['catalog']) {
  return catalog.reduce<Record<string, AiSettingsResponse['catalog']>>((acc, item) => {
    (acc[item.provider] ??= []).push(item);
    return acc;
  }, {});
}

function ModelPicker({
  idPrefix,
  label,
  choice,
  catalog,
  efforts,
  onChange
}: {
  idPrefix: string;
  label: string;
  choice: AiModelChoice;
  catalog: AiSettingsResponse['catalog'];
  efforts: string[];
  onChange: (choice: AiModelChoice) => void;
}) {
  const catalogByProvider = groupByProvider(catalog);

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-model`}>{label} model</Label>
        <select
          id={`${idPrefix}-model`}
          value={`${choice.provider}:${choice.model}`}
          onChange={e => {
            const [provider, ...rest] = e.target.value.split(':');
            onChange({ ...choice, provider: provider as AiModelChoice['provider'], model: rest.join(':') });
          }}
          className="border rounded px-2 py-1 text-sm w-full"
        >
          {Object.entries(catalogByProvider).map(([groupProvider, items]) => (
            <optgroup key={groupProvider} label={groupProvider}>
              {items.map(item => (
                <option key={`${item.provider}:${item.id}`} value={`${item.provider}:${item.id}`}>
                  {item.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-effort`}>{label} effort</Label>
        <select
          id={`${idPrefix}-effort`}
          value={choice.effort}
          onChange={e => onChange({ ...choice, effort: e.target.value as AiEffort })}
          className="border rounded px-2 py-1 text-sm w-full"
        >
          {efforts.map(e => (
            <option key={e} value={e}>
              {e}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

function TierRow({
  idPrefix,
  title,
  description,
  choice,
  catalog,
  efforts,
  onChangeChoice,
  onChangeMaxSteps
}: {
  idPrefix: string;
  title: string;
  description: string;
  choice: AiModelChoice | SteppedAiModelChoice;
  catalog: AiSettingsResponse['catalog'];
  efforts: string[];
  onChangeChoice: (choice: AiModelChoice) => void;
  onChangeMaxSteps?: (maxSteps: number) => void;
}) {
  return (
    <div className="space-y-3 rounded-md border p-4">
      <div>
        <h3 className="text-sm font-medium">{title}</h3>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <ModelPicker idPrefix={idPrefix} label={title} choice={choice} catalog={catalog} efforts={efforts} onChange={onChangeChoice} />
      {onChangeMaxSteps && (
        <div className="space-y-2 max-w-[160px]">
          <Label htmlFor={`${idPrefix}-max-steps`}>{title} max steps</Label>
          <Input
            id={`${idPrefix}-max-steps`}
            type="number"
            min={5}
            max={100}
            value={(choice as SteppedAiModelChoice).maxSteps}
            onChange={e => onChangeMaxSteps(Number(e.target.value))}
          />
        </div>
      )}
    </div>
  );
}

function AiInvestigateForm({ initial, catalog, efforts }: { initial: AiSettings; catalog: AiSettingsResponse['catalog']; efforts: string[] }) {
  const [settings, setSettings] = useState<AiSettings>(initial);
  const save = useSaveAiSettings();

  function updateTriage(choice: AiModelChoice) {
    setSettings(s => ({ ...s, triage: { ...s.triage, ...choice } }));
  }
  function updateTier<K extends keyof AiSettings['tiers']>(tier: K, choice: AiModelChoice) {
    setSettings(s => ({ ...s, tiers: { ...s.tiers, [tier]: { ...s.tiers[tier], ...choice } } }));
  }
  function updateTierMaxSteps(tier: 'light' | 'deep', maxSteps: number) {
    setSettings(s => ({ ...s, tiers: { ...s.tiers, [tier]: { ...s.tiers[tier], maxSteps } } }));
  }

  async function handleSave() {
    try {
      await save.mutateAsync(settings);
      toast.success('Settings saved');
    } catch (err) {
      toast.error((err as Error).message ?? 'Could not save settings');
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>AI Investigate</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <label className="inline-flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={settings.enabled}
            onChange={e => setSettings(s => ({ ...s, enabled: e.target.checked }))}
            className="h-4 w-4"
          />
          Enabled
        </label>

        <div className="space-y-3 rounded-md border p-4">
          <div>
            <label className="inline-flex items-center gap-2 text-sm font-medium">
              <input
                type="checkbox"
                checked={settings.triage.enabled}
                onChange={e => setSettings(s => ({ ...s, triage: { ...s.triage, enabled: e.target.checked } }))}
                className="h-4 w-4"
              />
              Triage
            </label>
            <p className="text-xs text-muted-foreground mt-1">
              A cheap first pass that decides how deep each investigation goes. When off, every ticket gets a deep investigation.
            </p>
          </div>
          <ModelPicker
            idPrefix="ai-settings-triage"
            label="Triage"
            choice={settings.triage}
            catalog={catalog}
            efforts={efforts}
            onChange={updateTriage}
          />
        </div>

        <div className="space-y-3">
          <h2 className="text-sm font-semibold">Tiers</h2>
          <div className="space-y-3">
            <TierRow
              idPrefix="ai-settings-reply"
              title="Answer directly (reply)"
              description="No tools — drafts a reply from the ticket alone"
              choice={settings.tiers.reply}
              catalog={catalog}
              efforts={efforts}
              onChangeChoice={choice => updateTier('reply', choice)}
            />
            <TierRow
              idPrefix="ai-settings-light"
              title="Light investigation"
              description="A short, focused investigation"
              choice={settings.tiers.light}
              catalog={catalog}
              efforts={efforts}
              onChangeChoice={choice => updateTier('light', choice)}
              onChangeMaxSteps={maxSteps => updateTierMaxSteps('light', maxSteps)}
            />
            <TierRow
              idPrefix="ai-settings-deep"
              title="Deep investigation"
              description="The full investigation"
              choice={settings.tiers.deep}
              catalog={catalog}
              efforts={efforts}
              onChangeChoice={choice => updateTier('deep', choice)}
              onChangeMaxSteps={maxSteps => updateTierMaxSteps('deep', maxSteps)}
            />
          </div>
        </div>

        <div className="flex items-center gap-3 pt-2">
          <Button onClick={handleSave} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
          {initial.updatedAt && <span className="text-xs text-muted-foreground">Last changed {new Date(initial.updatedAt).toLocaleString()}</span>}
        </div>
      </CardContent>
    </Card>
  );
}

export function AiSettingsView() {
  const { data, isLoading } = useAiSettings();

  return (
    <div className="container mx-auto max-w-4xl p-6 space-y-4">
      <h1 className="text-2xl font-semibold">Help Desk Settings</h1>
      {isLoading || !data ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <AiInvestigateForm initial={data.settings} catalog={data.catalog} efforts={data.efforts} />
      )}
    </div>
  );
}
